import { defineStore } from "pinia";
import { ref } from "vue";
import { SSEManager, SSEMessageResource } from "@/SSEManager";
import { apiClient } from "@/client";
import {
	Site,
	MapReviewQueryBody,
	MapReviewQueryEnvelope,
	MapReviewSiteRow,
} from "@/type/api";
import { useSessionStore } from "@/store/session";

// The Site resource's two client surfaces, which read different things:
//
//   - `query` is the review list's read — `QUERY /api/site` (doc/in-progress/
//     query.md §11 Phase 4): the Sites ledger's own rows, narrowed and paged by
//     the server, with the envelope's counts. It walks the envelope so the list
//     still holds every matching site, exactly as the fetch-all it replaces did;
//   - `fetchAll`/`fetchOne` are the Site fabric's full reads — the nested
//     Features a pool is found on, the contacts, the activity roll-up — which
//     the ledger row does not carry and Reveal.vue's pool view needs. They stay
//     on the resource's legacy GET, and `_byID` is their cache: a ledger row is
//     never seeded into it, so a pool read cannot mistake one for a full site.

export const useStoreSite = defineStore("site", () => {
	// State
	const _byID = ref<Map<number, Site>>(new Map());
	const loading = ref<boolean>(false);
	const error = ref<string | null>(null);

	// Subscription
	SSEManager.subscribe((msg: SSEMessageResource) => {
		if (msg.resource.startsWith("sync:site")) {
			if (msg.type == "deleted") {
				const uriParts = msg.uri.split("/");
				const id = parseInt(uriParts[uriParts.length - 1], 10);
				if (!isNaN(id)) {
					remove(id);
				}
			} else if (msg.type == "created" || msg.type == "updated") {
				const uriParts = msg.uri.split("/");
				const id = parseInt(uriParts[uriParts.length - 1], 10);
				if (!isNaN(id)) {
					fetchOne(id);
				}
			}
		}
	});
	SSEManager.subscribeReconnect(() => {
		_byID.value = new Map();
		fetchAll();
	});
	// Actions
	function all(): Site[] {
		return Array.from(_byID.value.values());
	}
	function byID(id: number): Site | undefined {
		return _byID.value.get(id);
	}

	/**
	 * The review list's page: every site the body's narrowing keeps, read
	 * through the generic QUERY endpoint and the envelope's own paging. There is
	 * no pager on this surface, so the walk is what keeps a search matching more
	 * sites than one page holds from being silently cut off; the envelope's
	 * `collection_total` is the denominator the count line shows and
	 * `filtered_total` is the narrowing's own size.
	 */
	async function query(
		body: MapReviewQueryBody,
	): Promise<MapReviewQueryEnvelope<MapReviewSiteRow>> {
		loading.value = true;
		error.value = null;
		try {
			return await apiClient.JSONQueryAll<MapReviewSiteRow>("/api/site", body);
		} catch (err) {
			error.value = err instanceof Error ? err.message : "Unknown error";
			console.error("Error loading sites:", err);
			throw err;
		} finally {
			loading.value = false;
		}
	}

	/**
	 * The Site fabric's whole-collection read, with the query string the caller
	 * states (Reveal.vue asks for the sites carrying a pool). It keeps its own
	 * `_byID` cache, which the full-shape consumers read.
	 */
	async function fetchAll(query?: Record<string, string>): Promise<Site[]> {
		const session = useSessionStore();
		if (session.urls == null) {
			throw new Error("can't fetch without user URL data");
		}

		loading.value = true;
		error.value = null;
		try {
			const params = new URLSearchParams();
			if (query) {
				for (const [k, v] of Object.entries(query)) {
					params.append(k, v);
				}
			}
			const response = await fetch(`${session.urls.api.site}?${params}`);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const data: Site[] = await response.json();
			_byID.value = new Map();
			for (const t of data) {
				_byID.value.set(t.id, t);
			}
			return data;
		} catch (err) {
			error.value = err instanceof Error ? err.message : "Unknown error";
			console.error("Error loading tasks:", err);
			throw err;
		} finally {
			loading.value = false;
		}
	}
	async function fetchOne(id: number) {
		const session = useSessionStore();
		if (session.urls == null) {
			throw new Error("can't fetch without user URL data");
		}

		loading.value = true;
		error.value = null;
		try {
			const response = await fetch(`${session.urls.api.site}/${id}`);

			if (!response.ok) {
				throw new Error(`HTTP error! status: ${response.status}`);
			}
			const data = await response.json();
			_byID.value.set(data.id, data);
			return data;
		} catch (err) {
			console.error("Error loading tasks:", err);
			throw err;
		}
	}
	function remove(id: number) {
		_byID.value.delete(id);
	}

	return {
		// State
		all,
		// Actions
		byID,
		query,
		fetchAll,
		fetchOne,
		remove,
	};
});
